import React from 'react';
import { View, StyleSheet, TouchableOpacity, Text, Image, Dimensions, Modal } from 'react-native';
import { TutorSelectionModal } from './TutorSelectionModal';
import { SettingsModal } from './SettingsModal';
import { Main } from './audiocall/main';
import { Tutor } from '../types';
import { ChatHeaderProps } from '../types/ChatHeaderProps';
import { useTutor } from '../contexts/TutorContext';
import { Ionicons } from '@expo/vector-icons';
import { ENABLE_AUDIO_CALL } from '../config';

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  setShowTutorModal,
  showTutorModal,
  isConfigModalVisible,
  setIsConfigModalVisible,
  userInfo,
  setUserInfo,
  setInterfaceLanguage,
}) => {
  const { selectedTutor, setSelectedTutor } = useTutor();
  const screenWidth = Dimensions.get('window').width;
  const [showAudioCall, setShowAudioCall] = React.useState(false);
  const [sessionId, setSessionId] = React.useState('');

  const handleTutorSelect = (tutor: Tutor) => {
    setSelectedTutor(tutor);
  };

  const handleStartCall = () => {
    setSessionId(Math.random().toString(36).substring(7));
    setShowAudioCall(true);
  };

  const handleEndCall = () => {
    setShowAudioCall(false);
    setSessionId('');
  };

  return (
    <View style={styles.headerWrapper}>
      <View style={styles.header}>
        {/* Left: Logo */}
        <View style={styles.leftSection}>
          <View style={styles.logoContainer}>
            <Image
              source={require('../../assets/logo.webp')}
              style={styles.logo}
              resizeMode="contain"
            />
          </View>
        </View>

        {/* Right: Controls Section */}
        <View style={styles.rightSection}>
          {/* Tutor Section */}
          <View style={styles.tutorSection}>
            {/* Tutor Label */}
            <View style={styles.tutorLabel}>
              <Ionicons name="school-outline" size={18} color="#6B7280" />
            </View>

            {/* Tutor Selector */}
            <TouchableOpacity 
              style={styles.tutorSelector}
              onPress={() => setShowTutorModal(true)}
            >
              {selectedTutor ? (
                <>
                  {selectedTutor.country_code && (
                    <Image
                      source={{ 
                        uri: `https://flagcdn.com/h24/${selectedTutor.country_code.toLowerCase()}.png`
                      }}
                      style={styles.flag}
                      resizeMode="contain"
                    />
                  )}
                  <Text style={styles.tutorName}>
                    {screenWidth <= 640 
                      ? selectedTutor.name.split(' ')[0]
                      : selectedTutor.name}
                  </Text>
                  <View style={styles.chevronContainer}>
                    <Ionicons name="chevron-down" size={18} color="#6B7280" />
                  </View>
                </>
              ) : (
                <>
                  <Text style={styles.selectTutorText}>Select a Tutor</Text>
                  <View style={styles.chevronContainer}>
                    <Ionicons name="chevron-down" size={18} color="#6B7280" />
                  </View>
                </>
              )}
            </TouchableOpacity>
          </View>

          {/* Settings Button */}
          <TouchableOpacity 
            style={styles.iconButton}
            onPress={() => setIsConfigModalVisible(true)}
          >
            <Ionicons name="menu-outline" size={24} color="#333" />
          </TouchableOpacity>

          {/* Audio Call Button */}
          {ENABLE_AUDIO_CALL && (
            <TouchableOpacity 
              style={[styles.iconButton, styles.audioCallButton]}
              onPress={handleStartCall}
              disabled={!selectedTutor}
            >
              <Ionicons 
                name="call-outline" 
                size={24} 
                color={selectedTutor ? "#4A90E2" : "#999"} 
              />
            </TouchableOpacity>
          )}
        </View>

        {/* Modals */}
        <TutorSelectionModal
          visible={showTutorModal}
          onClose={() => setShowTutorModal(false)}
          onSelectTutor={handleTutorSelect}
        />
        <SettingsModal
          visible={isConfigModalVisible}
          onClose={() => setIsConfigModalVisible(false)}
          userInfo={userInfo}
          setUserInfo={setUserInfo}
          setInterfaceLanguage={setInterfaceLanguage}
        />
        {showAudioCall && userInfo && (
          <Modal
            visible={showAudioCall}
            transparent={true}
            animationType="slide"
            onRequestClose={handleEndCall}
          >
            <View style={styles.modalOverlay}>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Audio Call</Text>
                  <TouchableOpacity onPress={handleEndCall} style={styles.closeButton}>
                    <Ionicons name="close" size={24} color="#666" />
                  </TouchableOpacity>
                </View>
                <Main
                  userInfo={userInfo}
                  onEndCall={handleEndCall}
                  sessionId={sessionId}
                />
              </View>
            </View>
          </Modal>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  headerWrapper: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    alignItems: 'center',
    flexDirection: 'column',
    padding: 0,
  },
  header: {
    width: '100%',
    maxWidth: 800,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingLeft: 0,
    paddingRight: 0,
    height: 72,
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 0,
    marginBottom: 0,
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    margin: 0,
    padding: 0,
  },
  logoContainer: {
    height: 67,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    margin: 0,
    padding: 0,
    left: 0,
  },
  logo: {
    width: 156,
    height: 58,
    marginLeft: -16,
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: -5,
    marginLeft: 5,
    marginTop: 0,
    marginBottom: 0,
    padding: 0,
  },
  tutorSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 4,
  },
  tutorLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 4,
  },
  tutorLabelText: {
    fontSize: 15,
    color: '#6B7280',
    fontWeight: '500',
    letterSpacing: 0.2,
  },
  tutorSelector: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    paddingVertical: 8,
    paddingLeft: 16,
    paddingRight: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
    alignSelf: 'flex-start',
  },
  flag: {
    width: 20,
    height: 14,
    borderRadius: 2,
  },
  tutorName: {
    fontSize: 15,
    color: '#374151',
    fontWeight: '500',
    flexShrink: 0,
    letterSpacing: 0.2,
  },
  selectTutorText: {
    fontSize: 15,
    color: '#6B7280',
    flexShrink: 1,
    letterSpacing: 0.2,
  },
  chevronContainer: {
    marginLeft: 'auto',
    paddingLeft: 4,
  },
  iconButton: {
    padding: 8,
    borderRadius: 8,
    marginLeft: 12,
  },
  audioCallButton: {
    backgroundColor: '#EBF5FF',
    borderWidth: 1,
    borderColor: '#4A90E2',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    width: '90%',
    maxWidth: 500,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
  },
  closeButton: {
    padding: 8,
  },
});
